<template>
  <v-container fluid class="editProfileContainer">
    <notifications group="actions"></notifications>
    <h2 class="display-1 font-weight-light">Edit &raquo;
      <router-link :to="{name: 'staffDetail', params: {id: $route.params.id}}" style="text-decoration: none">
        {{formFields.name}}
      </router-link>
    </h2>

    <v-form ref="editProfile" v-model="formIsValid" class="pa-sm-12">

      <v-card>
        <v-card-title class="primary lighten-2 mb-6">
          <h3 class="" id="basic">
            <v-icon>mdi-account</v-icon>
            Basic info
          </h3>
        </v-card-title>
        <v-card-subtitle>
          <h5 class="">All the information in this section is required</h5>
        </v-card-subtitle>
        <v-card-text>
          <v-row>
            <v-col class="col-12 col-sm-4 pa-sm-8 ">
              <v-text-field v-model="formFields.name" label="Name" required :rules="nameRules"/>
            </v-col>
            <v-col class="pa-sm-8 ">
              <v-text-field v-model="formFields.urlProvided" label="Linkedin url" required :rules="urlRules" disabled/>
            </v-col>
            <v-col class="pt-sm-8">
              <v-radio-group row v-model="formFields.status" label="Current Employee">
                <v-radio label="Yes" value="active"></v-radio>
                <v-radio label="No" value="inactive" @click="openEmployeeLeavingDialog()"></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
          <employee-leaving
            :selected-reasons="formFields.employeeLeavingReasons"
            ref="employeeLeavingDialog"
            @employee-leaving-submit="employeeLeavingCallback"
          ></employee-leaving>
        </v-card-text>
      </v-card>

      <v-card class="mt-4">
        <v-card-title class="primary lighten-3 mb-6">
          <h3 class="" id="job-search">
            <v-icon>mdi-tag</v-icon>
            Job info
          </h3>
        </v-card-title>
        <v-card-subtitle>
          <h5 class="">Put the search terms that this employee would use in order to search for a job.</h5>
          <p class="mb-0">You should select a job keyword from the list. If none of them fits your needs, please email us with your inquiry and we will study your case.</p>
        </v-card-subtitle>
        <v-card-text>

          <v-row no-gutters v-for="(item, i) in formFields.jobSearchTerms" :key="i">
            <v-col cols="11">
              <v-row>
                <v-col cols="12" class="col-sm-10 pa-sm-8">
                  <v-row no-gutters>
                    <v-col cols="4" class="px-2">
                      <v-autocomplete
                        v-model="item.jobKeywordName"
                        label="Select a job position"
                        :item-text="'jobKeywordName'"
                        :item-value="'jobKeywordName'"
                        :items="availableJobKeywords"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="2" class="px-2">
                      <v-text-field
                        v-if="item.jobKeywordName === 'Other'"
                        v-model="item.term"
                        :label="`Custom term`"
                        :rules="positionRules"
                        placeholder="Researcher, ..." required
                      />
                    </v-col>
                    <v-col cols="3">
                      <h5 class="d-flex">
                        Report years of experience
                        <v-tooltip top>
                          <template #activator="{on, attrs}">
                            <v-icon class="primary--text d-inline" v-on="on" v-bind="attrs">
                              mdi-information-outline
                            </v-icon>
                          </template>
                          You can now report the years' experience
                        </v-tooltip>
                        <v-switch
                          v-model="item.allowEditExperienceSince"
                          label=""
                          class="d-inline-flex mt-0 pt-0 pl-2"
                        ></v-switch>
                      </h5>
                      <v-row no-gutters>
                        <v-col cols="11" class="pt-2">
                          <v-slider
                            class="pr-3 mx-0"
                            v-model="item.yearsExperience"
                            thumb-label="always"
                            :hint="`Since ${getDateFromYears(item.yearsExperience)}`"
                            step="0.1"
                            min="0"
                            max="15"
                            persistent-hint
                            :disabled="!item.allowEditExperienceSince"
                          >
                            <template v-slot:prepend>
                              <v-icon
                                @click="item.yearsExperience = item.yearsExperience - 0.1"
                              >
                                mdi-minus
                              </v-icon>
                            </template>

                            <template v-slot:append>
                              <v-icon
                                @click="item.yearsExperience = item.yearsExperience + 0.1"
                              >
                                mdi-plus
                              </v-icon>
                            </template>
                          </v-slider>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="3">
                      <h5 class="">
                        Select a city
                        <v-tooltip top>
                          <template #activator="{on, attrs}">
                            <v-icon class="primary--text" v-on="on" v-bind="attrs">
                              mdi-information-outline
                            </v-icon>
                          </template>
                          <p class="mb-0">Chose a city or the closest city in the list</p>
                        </v-tooltip>
                      </h5>
                      <v-chip-group v-model="item.jobKeywordCity" column active-class="primary">
                        <v-chip v-for="(city, key) in listAvailableCities" :key="key" :value="city.city" small>{{city.city}}</v-chip>
                      </v-chip-group>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="12" class="col-sm-2 pa-sm-8">
                  <h5 class="">
                    Select a move
                    <v-tooltip top>
                      <template #activator="{on, attrs}">
                        <v-icon class="primary--text" v-on="on" v-bind="attrs">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <p class="mb-0">Use <b>"same position"</b> for related jobs to the person's current job. I.e.: if the current
                        job is <strong>senior ios developer</strong>, you could use <strong>ios developer</strong></p>
                      <p class="mb-0">Use <b>"upgrade"</b> for higher ranked jobs to the person's current job. I.e.: if the current
                        job is <strong>ios developer</strong>, you could use <strong>team lead</strong></p>
                      <p class="mb-0">Use <b>"pivotal"</b> for different jobs than the person's current job. I.e.: if the current
                        job is <strong>ios developer</strong>, you could use <strong>ux/ui designer</strong></p>
                    </v-tooltip>
                  </h5>
                  <v-chip-group v-model="item.shifting" column mandatory active-class="primary">
                    <v-chip v-for="(item, i) in getAvailableShiftings()" :value="item.field" :key="i" small
                            :disabled="item.disabled">{{item.display}}
                    </v-chip>
                  </v-chip-group>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="1" class="py-10 px-2">
              <v-btn icon @click="addJobSearchTerm()" v-if="canAddMoreJobSearchTerms">
                <v-icon color="success">mdi-plus</v-icon>
              </v-btn>
              <v-btn icon @click="removeJobSearchTerm(i)" v-if="formFields.jobSearchTerms.length > 1">
                <v-icon color="error">mdi-minus</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card class="mt-4">
        <v-card-title class="primary lighten-4 mb-6">
          <h3 class="" id="salary">
            <v-icon>mdi-currency-eur</v-icon>
            Salary info
          </h3>
        </v-card-title>
        <v-card-subtitle>
          <h5 class="">Set a range of salary for this employee. You can add perks or bonuses </h5>
        </v-card-subtitle>
        <v-card-text>
          <v-row no-gutters v-for="(item, i) in formFields.salary" :key="i">
            <v-col cols="12" v-if="i !== 0">
              <v-divider class="my-5"></v-divider>
            </v-col>
            <v-col cols="11">
              <v-row>
                <v-col cols="12" class="col-sm-3">
                  <v-menu
                    v-model="menuFrom[i]"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="item.from"
                        label="From"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="item.from"
                      @input="menuFrom[i] = false"
                      :max="new Date().toISOString().substr(0, 10)"
                    ></v-date-picker>
                  </v-menu>
                  <v-menu
                    v-model="menuTo[i]"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="item.to"
                        clearable
                        label="To"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="item.to"
                      @input="menuTo[i] = false"
                      :max="new Date().toISOString().substr(0, 10)"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" class="col-sm-2">
                  <v-select :items="currencies" v-model="item.salaryCurrency" label="Currency" required></v-select>

                </v-col>
                <v-col cols="12" class="col-sm-4">
                  <h5 class="mb-1">Salary Range</h5>
                  <v-chip-group v-model="item.baseSalaryRange" column active-class="primary">
                    <v-chip v-for="(item, i) in salaryRanges" :key="i" small :value="item">{{formatRange(item)}}
                    </v-chip>
                  </v-chip-group>
                </v-col>
                <v-col cols="12" class="col-sm-3">
                  <h5 class="mb-1">Bonus Range</h5>
                  <v-chip-group v-model="item.bonusSalaryRange" column active-class="primary">
                    <v-chip v-for="(item, i) in bonusRanges" :key="i" small :value="item">{{formatRange(item)}}</v-chip>
                  </v-chip-group>
                </v-col>
                <v-col cols="12" class="">
                  <h5 class="mb-1">Perks</h5>
                  <v-chip-group v-model="item.perks" column active-class="primary" multiple>
                    <v-tooltip top v-for="(item, i) in perks" :key="i">
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip small :value="item.display" v-bind="attrs" v-on="on">{{item.display}}</v-chip>
                      </template>
                      <span>{{item.tooltip}}</span>
                    </v-tooltip>
                  </v-chip-group>
                </v-col>

              </v-row>
            </v-col>
            <v-col cols="1">
              <v-btn icon @click="addSalaryItem()">
                <v-icon color="success">mdi-plus</v-icon>
              </v-btn>
              <v-btn icon @click="removeSalaryItem(i)" v-if="formFields.salary.length > 1">
                <v-icon color="error">mdi-minus</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card class="mt-4">
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <p v-if="formFields.status === 'inactive'">By setting "inactive" a member of your staff you acknowledge
                that Iceverg will stop tracking this profile.</p>
            </v-col>
            <v-col cols="12" class="text-center">
              <v-btn class="text-center" :disabled="!formIsValid" color="primary" rounded @click="editProfile"> SAVE
                CHANGES
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

    </v-form>
  </v-container>
</template>

<script>

  import moment from 'moment'
  import {formatRange, linkedinProfileUrl, nonEmptyField} from "../../helpers/helpers";
  import api from '@/api';
  import Vue from "vue";
  import {
    salaryDataStructure,
    salaryRanges,
    bonusRanges,
    currencies,
    perks,
    formFields,
    UPGRADE,
    PIVOTAL,
    maxSimilarPositions
  } from "../../constants/constants";
  import EmployeeLeaving from "../../components/Dialogs/Profiles/employeeLeaving";

  export default {
    name: "EditProfile",
    components: {EmployeeLeaving},
    data: () => ({
      formIsValid: false,
      menuFrom: [false],
      menuTo: [false],
      availableJobKeywords: [],
      formFields: [...formFields],
      salaryRanges,
      bonusRanges,
      perks,
      currencies,
      listAvailableCities: [],
      companyCity: '',
      nameRules: [v => nonEmptyField(v, 'Name')],
      urlRules: [v => nonEmptyField(v, 'Linkedin Url'), v => linkedinProfileUrl(v)],
      positionRules: [v => nonEmptyField(v, 'Job seach term')]
    }),
    mounted() {
      this.loadProfile();
      this.loadJobKeywords();
      this.loadAvailableJobOffersCities();
    },
    deactivated(){
      this.formFields = formFields;
    },
    methods: {
      loadJobKeywords(){
        api.jobKeywords.getListOfJobKeywordsNames()
          .then(response => {
            this.availableJobKeywords = response.data.data.jobKeywords.map(e => ({jobKeywordId: e._id, jobKeywordName: e.name}));
            this.availableJobKeywords.push({jobKeywordId: null, jobKeywordName: 'Other'});
          })
      },
      loadAvailableJobOffersCities() {
        api.jobKeywords.getListOfJobKeywordsCities()
          .then(response => {
            this.listAvailableCities = response.data.data.cities;
            this.companyCity = response.data.data.companyCity;
          })
      },
      loadProfile() {
        api.members.getDetails({_id: this.$route.params.id})
          .then(response => {
            this.formFields = response.data.data.profile;
            if (this.formFields.salary.length > 0) {
              this.formFields.salary.forEach(salary => {
                salary.from = salary.from ? new Date(salary.from).toISOString().substr(0, 10) : null;
                salary.to = salary.to ? new Date(salary.to).toISOString().substr(0, 10) : null;
                salary.baseSalaryRange = salary.baseSalaryRange ? salaryRanges.filter(e => e.min === salary.baseSalaryRange.min && e.max === salary.baseSalaryRange.max)[0] : salaryDataStructure.baseSalaryRange;
                salary.bonusSalaryRange = salary.bonusSalaryRange ? bonusRanges.filter(e => e.min === salary.bonusSalaryRange.min && e.max === salary.bonusSalaryRange.max)[0] : salaryDataStructure.bonusSalaryRange;
                //salary.perks = salary.perks || [];
              })
            } else {
              this.formFields.salary = [salaryDataStructure];
            }
            if(!this.formFields.jobSearchTerms ||
              (Array.isArray(this.formFields.jobSearchTerms) && this.formFields.jobSearchTerms.length <= 1 && !this.formFields.jobSearchTerms[0]) ) {
              this.formFields.jobSearchTerms = [...formFields.jobSearchTerms]
            }
            // preload experience
            if (this.formFields.jobSearchTerms[0].experienceSince === null) {
              const {edges} = response.data.data.profile
              if (edges && edges.times) {
                this.$set(this.formFields.jobSearchTerms[0], 'yearsExperience', Math.ceil(edges.times.timeInCurrentPosition / 12 * 10) / 10)
              }
            } else {
              const monthsDiff = moment().diff(moment(this.formFields.jobSearchTerms[0].experienceSince), 'months')
              this.$set(this.formFields.jobSearchTerms[0], 'yearsExperience', Math.ceil(monthsDiff / 12 * 10) / 10)
            }
          })
          .then(() => {
            if (JSON.parse(this.$route.query.leavingDialog) === true) {
              this.formFields.status = 'inactive';
              this.openEmployeeLeavingDialog();
            }
          })
      },
      addSalaryItem() {
        this.formFields.salary.push(salaryDataStructure);
        this.menuFrom.push(false);
        this.menuTo.push(false);
      },
      removeSalaryItem(index) {
        this.formFields.salary = this.formFields.salary.filter((e, k) => k !== index);
        this.menuFrom = this.menuFrom.filter((e, k) => k !== index);
        this.menuTo = this.menuTo.filter((e, k) => k !== index);
      },
      addJobSearchTerm() {
        const term = '';
        const shifting = this.getAvailableShiftings().filter(e => e.disabled !== true)[0].field;
        const jobKeywordName = '';
        const jobKeywordId = null;
        const jobKeywordCity = this.companyCity;
        this.formFields.jobSearchTerms.push({term, shifting, jobKeywordName, jobKeywordId, jobKeywordCity});
      },
      removeJobSearchTerm(index) {
        this.formFields.jobSearchTerms = this.formFields.jobSearchTerms.filter((e, k) => k !== index);
      },
      getAvailableShiftings() {
        const usedShiftings = this.formFields.jobSearchTerms.map(e => e.shifting);
        const noShiftings = usedShiftings.filter(e => e === null).length;
        return [
          {display: 'Same position', field: null, disabled: noShiftings >= maxSimilarPositions},
          {display: UPGRADE, field: UPGRADE, disabled: usedShiftings.indexOf(UPGRADE) > -1},
          {display: PIVOTAL, field: PIVOTAL, disabled: usedShiftings.indexOf(PIVOTAL) > -1},
        ];
      },
      formatRange(range) {
        return formatRange(range)
      },
      editProfile() {
        // we only want status, jobSearchTerms and salary to be sent to the server
        let profile = {
          _id: this.$route.params.id,
          status: this.formFields.status,
          jobSearchTerms: this.formFields.jobSearchTerms,
          salary: this.formFields.salary,
          name: this.formFields.name
        };

        let formIsValid = this.formIsValid
        profile.jobSearchTerms = profile.jobSearchTerms.map(e => {
          let elem = {...e};
          // let notOtherJobKeyword = this.availableJobKeywords.find(k => k.jobKeywordName === e.jobKeywordName)
          // if (notOtherJobKeyword) {
          //   elem.jobKeywordId = notOtherJobKeyword.jobKeywordId;
          // }
          elem.shifting = (e.shifting === 0) ? null : e.shifting;
          if (elem.allowEditExperienceSince) {
            elem.experienceSince = new Date(moment().subtract(elem.yearsExperience, 'years').toISOString())
          }
          if (!elem.jobKeywordCity) {
            Vue.notify({
              group: 'actions',
              type: 'error',
              text: 'You must select a city'
            });
            formIsValid = false
          }
          return elem;
        });
        if (this.formFields.employeeLeavingReasons) {
          profile.employeeLeavingReasons = this.formFields.employeeLeavingReasons;
        }

        if (!formIsValid) {
          return
        }

        api.members.editProfile(profile)
          .then(response => {
            Vue.notify({
              group: 'actions',
              type: 'success',
              text: 'Member edited successfully'
            });
            this.$router.push({name: 'staffDetail', params: {id: this.$route.params.id}})
            // redirect to the list
          }).catch(error => {
          Vue.notify({
            group: 'actions',
            type: 'error',
            text: 'Member could not be edited.'
          });
        })
      },
      openEmployeeLeavingDialog() {
        this.$refs.employeeLeavingDialog.open = true;
      },
      employeeLeavingCallback(event) {
        this.formFields.employeeLeavingReasons = event.employeeLeavingReasons;
      },
      getJobKeywordLabel(index) {
        return index === 0 ? 'Main job keyword' : 'Another job keyword';
      },
      getDateFromYears(years) {
        if (years) {
          return moment().subtract(years, 'years').format('MMMM YYYY')
        }
        return ''
      }
    },
    computed: {
      canAddMoreJobSearchTerms() {
        const usedShiftings = this.formFields.jobSearchTerms.map(e => e.shifting);
        return usedShiftings.length < 5;
      }
    }
  }
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/views/staff/EditProfile.vue